import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy,
  ViewContainerRef,
  AfterViewInit,
} from '@angular/core';
import {
  forkJoin as observableForkJoin,
  Observable,
  of,
  Subject,
  forkJoin,
  firstValueFrom,
} from 'rxjs';
import {
  mergeMap,
  filter,
  catchError,
  takeUntil,
  debounceTime,
  map,
  tap,
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ParamMap, ActivatedRoute, Router } from '@angular/router';
import { ClientService } from '../../../core/services/client.service';
import {
  Client,
  ClientDataSubscriptionResult,
  ClientLastLogin,
} from '../../../core/types/client';
import { BankAccount } from '../../../core/types/bank-account';
import { FormComponentData } from '../../../core/types/form-component-data';
import { Utils } from '../../../core/utils/utils';
import { ClientStatus } from '../../../core/types/client-status';
import { BrandService } from '../../../core/services/brand.service';
import { Brand, BrandCodes } from '../../../core/types/brand';
import { NotificationService } from '../../../core/services/notification.service';
import {
  ClientDocument,
  IdentityDocument,
} from '../../../core/types/client-document';
import { LoaderService } from '../../../core/services/loader.service';
import { FormManipulationService } from '../../../core/services/form-manipulation.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from '../../../core/services/error.service';
import { LoyaltyPointsService } from '../../../core/services/loyalty-points.service';
import { LoyaltyPointsFlowComponent } from '../loyalty-points/loyalty-points-flow/loyalty-points-flow.component';
import { LoyaltyPointsFlow } from '../../../core/types/loyalty-points-flow';
import { AddTagComponent } from '../../tags/add-tag/add-tag.component';
import { Tag, TagTypeNames } from '../../../core/types/tag';
import { TagService } from '../../../core/services/tag.service';
import { SmsService } from '../../../core/services/sms.service';
import { EmailService } from '../../../core/services/email.service';
import { ContactService } from '../../../core/services/contacts.service';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { EventLogStateService } from '@app/main/main-layout/right-drawer/event-log/event-log-state.service';
import {
  EventLogHistoryType,
  EventLogMode,
  EventLogSubheader,
} from '@app/main/main-layout/right-drawer/shared/event-log.enums';
import { FilterGroupType } from '@app/main/filter/types';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { saveAs } from 'file-saver';
import { DateFormat } from '@app/core/types/date-format';
import { TagDropdownComponent } from '@app/main/tags/tags-dropdown/tags-dropdown.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { ClientPricingOfferCreateDialogComponent } from '@app/main/client-pricing-offer/client-pricing-offer-create-dialog.component/client-pricing-offer-create-dialog.component';
import { SearchDirection, SearchOptions } from '@app/core/types/search-options';
import { SearchOperations } from '@app/core/types/search-criterium';
import { ClientPricingOfferService } from '@app/core/services/client-pricing-offer.service';
import { ClientPricingOffer } from '@app/core/types/client-pricing-offer';
import { ContactPhone, ContactPhoneDto } from '@app/core/types/contact-phone';
import { EventLogService } from '@app/core/services/event-log.service';
import { IdCardImageTypes } from '@app/core/types/identity-verification';

@Component({
  selector: 'itfg-client-edit',
  templateUrl: './client-edit.component.html',
  styleUrls: ['./client-edit.component.scss'],
  standalone: false
})
export class ClientEditComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() clientData: Client;
  @Input() loyaltyPointsAvailableNumberInput: number;
  @Input() public tabSelection: Observable<MatTabChangeEvent>;
  @Output() outputClientData = new EventEmitter<Client>();
  _unsubscribe = new Subject<void>();

  @ViewChild('profileElement') profileEl: ElementRef;
  @ViewChild('profileComponent') profileComponentEl;
  @ViewChild('personalIdElement') personalIdEl: ElementRef;
  @ViewChild('personalIdComponent') personalIdComponentEl;
  @ViewChild('personalElement') personalEl: ElementRef;
  @ViewChild('personalComponent') personalComponentEl;
  @ViewChild('occupationStatusElement')
  occupationStatusEl: ElementRef;
  @ViewChild('occupationStatusComponent')
  occupationStatusComponentEl;
  @ViewChild('tagMenuTrigger') tagMenuTrigger: MatMenuTrigger;
  @ViewChild('tagMenuComponent') tagMenuComponentEl: TagDropdownComponent;
  @ViewChild('contactElement') contactEl: ElementRef;
  @ViewChild('contactComponent') contactComponentEl;
  @ViewChild('bankAccountsEl') bankAccountsEl: ElementRef;
  @ViewChild('bankAccountComponentEl') bankAccountComponentEl;
  @ViewChild('documentsEl') documentsEl: ElementRef;
  @ViewChild('documentsComponentEl') documentsComponentEl;

  isNewItem: boolean = true;
  client: Client = new Client({});
  isFormValid: any = {};
  isFormValidAfterSave: boolean;
  clientStatuses: ClientStatus[];
  brandData: Brand[];
  actualClient: { email: string; mobile: string };

  public readonly TAGS_LIMIT = 15;
  tagTypeNames: typeof TagTypeNames = TagTypeNames;

  bankAccounts: any;
  initialBankAccounts: BankAccount[];
  clientDocuments: any;
  erroredBankAccounts: any;
  deletedBankAccountIndex: number;
  bankAcountsFormData: any[];
  invalidProfileData: boolean;
  clientComponentData: {
    status: any;
    personalData: any;
    profileData: any;
    idCardDataAndAddress: any;
    occupationStatus: any;
    bankAccounts: any;
    documents: any;
    identityDocuments: any;
  };

  clientDataChangesDetected: boolean;
  enableSaveButton: boolean;
  tabSelected: boolean;
  isClientInput: boolean;
  lastLogin: ClientLastLogin;
  loyaltyPointsAvailableNumber: number;
  loyaltyPointsFlow: LoyaltyPointsFlow;
  applicationId: string;
  isInClientEdit: boolean = false;
  isLoadingData: boolean = false;
  identityDocumentsColumns = [];
  clientOffers: ClientPricingOffer[];
  activeClientOffers: ClientPricingOffer[];
  fromNowDateFormat = DateFormat.FROM_NOW;

  constructor(
    protected clientService: ClientService,
    protected contactService: ContactService,
    protected tagService: TagService,
    protected brandDataService: BrandService,
    protected translateService: TranslateService,
    protected formBuilder: UntypedFormBuilder,
    protected notificationService: NotificationService,
    protected route: ActivatedRoute,
    protected router: Router,
    public dialog: MatDialog,
    public loaderService: LoaderService,
    public dialogService: DialogProviderService,
    public viewContainerRef: ViewContainerRef,
    protected formManipulationService: FormManipulationService,
    protected errorService: ErrorService,
    protected loyaltyPointsService: LoyaltyPointsService,
    protected smsService: SmsService,
    protected emailService: EmailService,
    protected eventLogStateService: EventLogStateService,
    protected identityReportsService: IdentityReportsService,
    protected translate: TranslateService,
    protected clientPricingOfferService: ClientPricingOfferService,
    protected eventLogService: EventLogService
  ) {
    this.clientComponentData = this.createClientComponentData();
    this.clientStatuses = [];
    this.bankAcountsFormData = [];
    this.bankAccounts = {};
    this.clientDocuments = {};
    this.erroredBankAccounts = [];
    this.initialBankAccounts = [];
    this.invalidProfileData = false;
    this.clientDataChangesDetected = false;
    this.enableSaveButton = false;
    this.isInClientEdit = this.router.url.includes('clients');
    this.sendPasswordBySms = this.sendPasswordBySms.bind(this);
    this.sendPasswordByEmail = this.sendPasswordByEmail.bind(this);
    this.openCreateOfferDialog = this.openCreateOfferDialog.bind(this);
    this.setIdentityDocumentColumns();
  }

  setIdentityDocumentColumns() {
    this.translate
      .get([
        'identityDocuments.columns.createdAt',
        'identityDocuments.columns.type',
        'identityDocuments.columns.name',
        'identityDocuments.columns.uploaderInfo',
        'global.actions',
      ])
      .subscribe((translation) => {
        this.identityDocumentsColumns = [
          {
            name: 'createdAt',
            label: translation['identityDocuments.columns.createdAt'],
          },
          {
            name: 'type.name',
            label: translation['identityDocuments.columns.type'],
          },
          {
            name: 'name',
            label: translation['identityDocuments.columns.name'],
          },
          {
            name: 'uploaderInfo',
            label: translation['identityDocuments.columns.uploaderInfo'],
          },
          {
            name: 'actions',
            label: translation['global.actions'],
          },
        ];
      });
  }

  downloadIdentityDocumentClicked(event: Event, document: IdentityDocument) {
    event.stopPropagation();
    this;
    this.identityReportsService
      .renderIdentityDocumentContent(this.client.civilId, document.id)
      .subscribe((doc) => {
        const blob = new Blob(<any>[doc]);
        saveAs(blob, document.name);
      });
  }

  observeClientDataChanges() {
    this.clientService.onClientDataChange
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(this.handleClientDataChanged.bind(this));
    this.identityReportsService.onIdentityDocumentChange
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((documents: IdentityDocument[]) => {
        this.clientComponentData.identityDocuments = documents;
      });
  }

  handleClientDataChanged(client: Client) {
    this.client = client;
    this.clientComponentData.profileData = this.profileDataParse(this.client);
    this.clientComponentData.personalData = this.personalDataParse(this.client);
    this.clientComponentData.idCardDataAndAddress =
      this.idCardDataAndAddressParse(this.client);
    this.clientComponentData.occupationStatus = this.occupationStatusDataParse(
      this.client
    );
  }

  observeClientOccupationDataChanges() {
    this.clientService.onClientOcupationDataChange
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((client) => {
        this.client = client;
        this.clientComponentData.occupationStatus =
          this.occupationStatusDataParse(this.client);
      });
  }

  mapIdentityDocumentsToClientData$(result: ClientDataSubscriptionResult) {
    return this.identityReportsService
      .getIdentityDocumentList$(
        result.client.civilId,
        <BrandCodes>result.client.brand.code
      )
      .pipe(
        map((identityDocuments: IdentityDocument[]) => {
          return {
            client: result.client,
            bankAccounts: result.bankAccounts,
            clientDocuments: result.clientDocuments,
            identityDocuments,
            lastLogin: result.lastLogin,
            loyaltyPoints: result.loyaltyPoints,
          };
        })
      );
  }

  mapClientDataSubscriptions$(params: ParamMap) {
    const id: string = params.get('id');
    this.isLoadingData = true;
    return forkJoin({
      client: this.clientService.getClientById(id),
      bankAccounts: this.clientService.getClientBankAccounts(id),
      clientDocuments: this.clientService.getClientDocumentList(id),
      lastLogin: this.clientService.getClientLastLogin$(id),
      loyaltyPoints: this.loyaltyPointsService.getLoyaltyAvailableNumber$(id),
    }).pipe(
      mergeMap(this.mapIdentityDocumentsToClientData$.bind(this)),
      mergeMap(this.mapClientOffersToData$.bind(this))
    );
  }

  mapClientOffersToData$(result: any) {
    const baseSearchOptions = new SearchOptions();
    baseSearchOptions.page = 0;
    baseSearchOptions.pageSize = 1000;
    baseSearchOptions.direction = SearchDirection.DESCENDING;
    baseSearchOptions.column = 'id';
    baseSearchOptions.criteria = [
      {
        key: 'civilId',
        operation: SearchOperations.EQUALS,
        value: result.client.civilId,
      },
      {
        key: 'brand.code',
        operation: SearchOperations.EQUALS,
        value: result.client.brand.code
      },
    ];

    const activeSearchOptions = new SearchOptions();
    activeSearchOptions.page = 0;
    activeSearchOptions.pageSize = 1000;
    activeSearchOptions.direction = SearchDirection.DESCENDING;
    activeSearchOptions.column = 'id';
    activeSearchOptions.criteria = [
      ...baseSearchOptions.criteria,
      {
        key: 'cancelledAt',
        operation: SearchOperations.EQUALS,
        value: null
      },
      {
        key: 'validTo',
        operation: SearchOperations.GREATER_THAN_OR_EQUAL,
        value: new Date().toISOString()
      }
    ];

    return forkJoin({
      allOffers: this.clientPricingOfferService.getClientPricingOfferList$(baseSearchOptions.getDTO()),
      activeOffers: this.clientPricingOfferService.getClientPricingOfferList$(activeSearchOptions.getDTO())
    }).pipe(
      map(({ allOffers, activeOffers }) => {
        return {
          client: result.client,
          bankAccounts: result.bankAccounts,
          clientDocuments: result.clientDocuments,
          identityDocuments: result.identityDocuments,
          lastLogin: result.lastLogin,
          loyaltyPoints: result.loyaltyPoints,
          clientPricingOffers: allOffers.content,
          activeClientOffers: activeOffers.content
        };
      })
    );
  }

  observeIdParamChanges() {
    this.route.paramMap
      .pipe(
        filter((params: ParamMap) => typeof params.get('id') === 'string'),
        mergeMap(this.mapClientDataSubscriptions$.bind(this)),
        filter(({ client }) => client !== null),
        takeUntil(this._unsubscribe)
      )
      .subscribe(
        ({ client, bankAccounts, clientDocuments, identityDocuments, lastLogin, loyaltyPoints, clientPricingOffers, activeClientOffers }) => {
          this.lastLogin = lastLogin;
          this.loyaltyPointsAvailableNumber = loyaltyPoints;
          this.setClientDataById(client, bankAccounts, clientDocuments, identityDocuments, clientPricingOffers, activeClientOffers);
        }
      );
  }

  observeClientBankAccountChanges() {
    this.clientService.clientBankAccounts$
      .pipe(
        takeUntil(this._unsubscribe),
        tap(() => {
          this.isLoadingData = true;
        })
      )
      .subscribe((bankAccountList: BankAccount[]) => {
        observableForkJoin({
          bankAccounts: of(bankAccountList),
          clientDocuments: this.clientService.getClientDocumentList(
            this.clientData.id
          ),
          identityDocuments: this.identityReportsService
            .getIdentityDocumentList$(
              this.clientData.civilId,
              <BrandCodes>this.clientData.brand.code
            )
            .pipe(catchError((e) => of(e.message))),
        })
          .pipe(takeUntil(this._unsubscribe))
          .subscribe(
            (result: {
              bankAccounts: BankAccount[];
              clientDocuments: ClientDocument[];
              identityDocuments: IdentityDocument[];
            }) => {
              this.setClientDataById(
                this.clientData,
                result.bankAccounts,
                result.clientDocuments,
                result.identityDocuments,
                this.clientOffers
              );
            }
          );
      });
  }

  observeParamMapApplicationRefChanges() {
    this.route.queryParamMap
      .pipe(
        takeUntil(this._unsubscribe),
        filter(
          (paramMap: ParamMap) =>
            typeof paramMap.get('applicationRef') === 'string'
        ),
        map((paramMap: ParamMap) => paramMap.get('applicationRef'))
      )
      .subscribe((applicationRefId: string) => {
        this.applicationId = applicationRefId;
      });
  }


  observeLoyaltyPointsFlowSubject() {
    this.loyaltyPointsService.loyaltyPointsFlowSubject
      .pipe(
        debounceTime(1000), // TODO: ask Stoil credits returns responce before deleting points
        mergeMap((_) =>
          this.loyaltyPointsService.getLoyaltyAvailableNumber$(this.client.id)
        ),
        takeUntil(this._unsubscribe)
      )
      .subscribe((loyaltyPointsNumber) => {
        this.loyaltyPointsAvailableNumber = loyaltyPointsNumber;
      });
  }

  ngOnInit() {
    this.observeClientDataChanges();
    this.observeClientOccupationDataChanges();
    this.observeIdParamChanges();
    this.observeParamMapApplicationRefChanges();
    this.observeClientBankAccountChanges();
    this.getClientStatuses();
    this.getBrandList();
    this.observeLoyaltyPointsFlowSubject();
  }

  ngAfterViewInit() {
    if (!!this.clientData) {
      this.markInvalidForm(
        this.profileComponentEl.profileDataForm,
        this.profileEl.nativeElement
      );
      this.markInvalidForm(
        this.personalComponentEl.personalDataForm,
        this.personalEl.nativeElement
      );
      this.markInvalidForm(
        this.personalIdComponentEl.profileDataForm,
        this.personalIdEl.nativeElement
      );
      this.markInvalidForm(
        this.occupationStatusComponentEl.occupationStatusForm,
        this.occupationStatusEl.nativeElement
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loyaltyPointsAvailableNumberInput) {
      this.loyaltyPointsAvailableNumber =
        this.loyaltyPointsAvailableNumberInput;
    }

    if (changes.clientData && this.clientData && this.clientData.id) {
      this.isClientInput = true;
      this.setLastLogin(this.clientData.id);
      this.actualClient = {
        email: this.client.email,
        mobile: this.client.mobile,
      };
      this.getOffersByClientCivilId();
      if (changes.clientData?.currentValue) { // hotfix: personal data gets back to initial after user updated form the big green save button
        this.handleClientDataChanged(changes.clientData?.currentValue);
      }
    }

    if (changes.tabSelection && this.tabSelection) {
      this.tabSelection
        .pipe(
          filter((tabChanged: MatTabChangeEvent) => tabChanged.index === 1),
          takeUntil(this._unsubscribe)
        )
        .subscribe((tabChanged: MatTabChangeEvent) => {
          setTimeout(() => {
            this.tabSelected = true;
          }, 0);
        });
    }
  }

  setClientDataById(
    client: Client,
    bankAccounts,
    clientDocuments: ClientDocument[],
    identityDocuments: IdentityDocument[],
    clientPricingOffers?: ClientPricingOffer[],
    activeClientOffers?: ClientPricingOffer[],
  ) {
    this.isNewItem = false;
    this.client = client;
    this.bankAccounts.data = bankAccounts;
    this.clientDocuments.data = clientDocuments;
    this.clientComponentData = this.breakdownClientForComponents(
      client,
      bankAccounts,
      clientDocuments,
      identityDocuments
    );
    this.clientDataChangesDetected = false;
    this.enableSaveButton = false;

    if (this.isInClientEdit) {
      this.clientLogInit();
    }

    if (clientPricingOffers !== undefined) {
      this.clientOffers = clientPricingOffers;
    }
    if (activeClientOffers !== undefined) {
      this.activeClientOffers = activeClientOffers;
    }

    this.isLoadingData = false;
  }

  statusDataChanged(status: ClientStatus) {
    this.clientDataChangesDetected = true;
    this.enableSaveButton =
      true && Object.values(this.isFormValid).every(Boolean);
    this.client.status = status;
    this.clientComponentData.status = status;
  }

  getBrandList() {
    this.brandDataService
      .getBrandList()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((response) => {
        this.brandData = response;
      });
  }

  getClientStatuses() {
    this.clientService
      .getClientStatusList()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((statusList) => {
        this.clientStatuses = statusList;
      });
  }

  setLastLogin(userId: any) {
    this.clientService
      .getClientLastLogin$(userId)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((lastLogin) => {
        this.lastLogin = lastLogin;
      });
  }

  createClientComponentData() {
    return {
      status: {
        id: 0,
        name: '',
        reason: '',
      },
      brand: {
        active: null,
        code: '',
        id: null,
        name: '',
      },
      personalData: {
        civilId: '',
        firstName: '',
        middleName: '',
        lastName: '',
        nationality: '',
        secondNationality: '',
        maritalStatus: {},
      },
      profileData: {
        email: '',
        ip: '',
        emailConfirmed: false,
        mobile: '',
        mobileConfirmed: false,
        pep: false,
        loyaltyEnabled: false,
        facebookId: '',
        googleId: '',
      },
      idCardDataAndAddress: {
        idCardNumber: '',
        idCardIssuedDate: '',
        idCardVerified: false,
        idCardAddress: '',
        idCardIssuer: '',
        currentAddress: '',
      },
      occupationStatus: {
        employerId: '',
        employerName: '',
        workPosition: '',
        workAddress: '',
        workExperience: '',
        workExperienceTotal: '',
        employmentType: {},
        education: '',
        bankTransferSalary: '',
        salary: '',
        extraIncomeNotes: '',
        extraIncome: '',
      },
      bankAccounts: {
        bankAccounts: '',
      },
      documents: [],
      identityDocuments: [],
    };
  }

  breakdownClientForComponents(
    client: Client,
    bankAccounts?: BankAccount[],
    clientDocuments?: ClientDocument[],
    identityDocuments?: IdentityDocument[]
  ) {
    return {
      ...this.breakdownClientData(client),
      bankAccounts: this.bankAccountDataParse(bankAccounts),
      documents: clientDocuments,
      identityDocuments,
    };
  }

  breakdownClientData(client: Client) {
    return {
      status: client.status,
      brand: client.brand,
      personalData: this.personalDataParse(client),
      profileData: this.profileDataParse(client),
      idCardDataAndAddress: this.idCardDataAndAddressParse(client),
      occupationStatus: this.occupationStatusDataParse(client),
    };
  }

  personalDataParse(client: Client): any {
    return {
      civilId: client.civilId,
      firstName: client.firstName,
      middleName: client.middleName,
      lastName: client.lastName,
      nationality: client.nationality,
      secondNationality: client.secondNationality,
      maritalStatus: client.maritalStatus,
    };
  }

  personalDataChanged(personalData: FormComponentData) {
    this.client.civilId = personalData.data.civilId;
    this.client.firstName = personalData.data.firstName;
    this.client.middleName = personalData.data.middleName;
    this.client.lastName = personalData.data.lastName;
    this.client.nationality = personalData.data.nationality;
    this.client.secondNationality = personalData.data.secondNationality;
    this.client.maritalStatus = personalData.data.maritalStatus;

    this.isFormValid.personalData = personalData.valid;
    this.clientDataChangesDetected = personalData.dirty;
    this.enableSaveButton = Object.values(this.isFormValid).every(Boolean);
  }

  profileDataParse(client: Client): any {
    return {
      email: client.email,
      brand: client.brand,
      ip: client.ip,
      emailConfirmed: client.emailConfirmed,
      mobile: client.mobile,
      mobileConfirmed: client.mobileConfirmed,
      pep: client.pep,
      loyaltyEnabled: client.loyaltyEnabled,
      facebookId: client.facebookId,
      googleId: client.googleId,
    };
  }

  profileDataChanged(profileData: FormComponentData) {
    this.client.email = profileData.data.email;
    this.client.emailConfirmed = profileData.data.emailConfirmed;
    this.client.mobile = profileData.data.mobile;
    this.client.mobileConfirmed = profileData.data.mobileConfirmed;
    this.client.pep = profileData.data.pep;
    this.client.loyaltyEnabled = profileData.data.loyaltyEnabled;
    this.client.brand = profileData.data.brand;
    this.client.facebookId = profileData.data.facebookId;
    this.client.googleId = profileData.data.googleId;

    this.isFormValid.profileData = profileData.valid;
    this.clientDataChangesDetected = profileData.dirty;
    this.enableSaveButton = Object.values(this.isFormValid).every(Boolean);
  }

  idCardDataAndAddressParse(client: Client): any {
    return {
      idCardNumber: client.idCardNumber,
      idCardIssuedDate: client.idCardIssuedDate,
      idCardVerified: client.idCardVerified,
      idCardAddress: client.idCardAddress,
      idCardIssuer: client.idCardIssuer,
      currentAddress: client.currentAddress,
    };
  }

  idCardDataAndAddressChanged(idCardDataAndAddress: FormComponentData) {
    this.client.idCardNumber = idCardDataAndAddress.data.idCardNumber;
    this.client.idCardIssuer = idCardDataAndAddress.data.idCardIssuer;
    this.client.idCardVerified = idCardDataAndAddress.data.idCardVerified;
    this.client.idCardIssuedDate = idCardDataAndAddress.data.idCardIssuedDate;
    this.client.idCardAddress = idCardDataAndAddress.data.idCardAddress;
    this.client.currentAddress = idCardDataAndAddress.data.currentAddress;

    this.isFormValid.idCardDataAndAddress = idCardDataAndAddress.valid;
    this.clientDataChangesDetected = idCardDataAndAddress.dirty;
    this.enableSaveButton = Object.values(this.isFormValid).every(Boolean);
  }

  occupationStatusDataParse(client: Client): any {
    return {
      employerId: client.employerId,
      employerName: client.employerName,
      workPosition: client.workPosition,
      workAddress: client.workAddress,
      workExperience: client.workExperience,
      workExperienceTotal: client.workExperienceTotal,
      employmentType: client.employmentType,
      education: client.education,
      bankTransferSalary: client.bankTransferSalary,
      salary: client.salary,
      extraIncomeNotes: client.extraIncomeNotes,
      extraIncome: client.extraIncome,
    };
  }

  occupationStatusDataChanged(occupationStatusData: FormComponentData) {
    this.client.workAddress = this.client.workAddress
      ? this.client.workAddress
      : {};
    if (this.client.id) {
      if (occupationStatusData.data.workAddress.city) {
        this.client.workAddress.city =
          occupationStatusData.data.workAddress.city;
        if (occupationStatusData.data.workAddress.address) {
          this.client.workAddress.address =
            occupationStatusData.data.workAddress.address;
        } else {
          this.client.workAddress.address = '';
        }
      } else {
        this.client.workAddress = null;
      }
    }

    this.client.employerId = occupationStatusData.data.employerId;
    this.client.employerName = occupationStatusData.data.employerName;
    this.client.workPosition = occupationStatusData.data.workPosition;
    this.client.workExperience = occupationStatusData.data.workExperience;
    this.client.workExperienceTotal =
      occupationStatusData.data.workExperienceTotal;
    this.client.employmentType = occupationStatusData.data.employmentType;
    this.client.education = occupationStatusData.data.education;
    this.client.bankTransferSalary =
      occupationStatusData.data.bankTransferSalary;
    this.client.salary = occupationStatusData.data.salary;
    this.client.extraIncomeNotes = occupationStatusData.data.extraIncomeNotes;
    this.client.extraIncome = occupationStatusData.data.extraIncome;

    this.isFormValid.occupationStatusData = occupationStatusData.valid;
    this.clientDataChangesDetected = occupationStatusData.dirty;
    this.enableSaveButton = Object.values(this.isFormValid).every(Boolean);
  }

  bankAccountDataParse(bankAccounts: BankAccount[]): any {
    return {
      bankAccounts,
    };
  }

  bankAccountsDataChanged(bankAccountsData: FormComponentData) {
    this.isFormValid.bankAccountsData = bankAccountsData.valid;
    this.clientDataChangesDetected = bankAccountsData.dirty;
    this.enableSaveButton = Object.values(this.isFormValid).every(Boolean);
  }

  contactDataChanged(contactData: FormComponentData) {
    this.isFormValid.contactData = contactData.valid;
    this.clientDataChangesDetected = contactData.dirty;
    this.enableSaveButton = Object.values(this.isFormValid).every(Boolean);
  }

  onBankAccountDocumentEvent(event: { action: 'added' | 'deleted', document: ClientDocument }) {
    if (event.action === 'added') {
      const docExists = this.clientComponentData.documents?.some(
        (doc: ClientDocument) => doc.id === event.document.id
      );

      if (!docExists) {
        this.clientComponentData.documents?.push(event.document);
        this.clientService.onDataChange.next(this.clientComponentData.documents);
      }
    } else if (event.action === 'deleted') {
      const docIndex = this.clientComponentData.documents.findIndex(
        (doc: ClientDocument) => doc.id === event.document.id
      );

      if (docIndex !== -1) {
        this.clientComponentData.documents?.splice(docIndex, 1);
        this.clientService.onDataChange.next(this.clientComponentData.documents);
      }
    }
  }

  documentDataChanged(documentsData: any): any {
    this.clientDocuments = documentsData;
    this.clientDataChangesDetected = documentsData.dirty;
  }

  deleteClientDocument(clientDocumet: ClientDocument) {
    for (
      let i = 0;
      i < this.clientComponentData.bankAccounts.bankAccounts.length;
      i++
    ) {
      const bankAccountDocument =
        this.clientComponentData.bankAccounts.bankAccounts[i].userDocument;

      if (bankAccountDocument && clientDocumet.id === bankAccountDocument.id) {
        this.clientComponentData.bankAccounts.bankAccounts[i].userDocument =
          null;
        this.clientService.onBankAccoutDataChange.next(
          this.clientComponentData.bankAccounts
        );
      }
    }

    const handleDocumentDeleted = (response) => {
      for (let i = 0; i < this.clientComponentData.documents.length; i++) {
        const document = this.clientComponentData.documents[i];

        if (clientDocumet.id === document.id) {
          this.clientComponentData.documents.splice(i, 1);
          this.clientService.onDataChange.next(
            this.clientComponentData.documents
          );
        }
      }
    };

    const identityDocumentAllowedTypes = [
      IdCardImageTypes.ID_BACK,
      IdCardImageTypes.ID_FRONT,
      IdCardImageTypes.SELFIE,
    ];

    if (
      clientDocumet.hasOwnProperty('type') &&
      identityDocumentAllowedTypes.includes(clientDocumet['type'])
    ) {
      this.identityReportsService
        .deleteIdentityDocument(this.client.civilId, clientDocumet.id)
        .pipe(takeUntil(this._unsubscribe))
        .subscribe(handleDocumentDeleted.bind(this));
    } else {
      this.clientService
        .deleteClientDocument(this.client.id, clientDocumet.id)
        .pipe(takeUntil(this._unsubscribe))
        .subscribe(handleDocumentDeleted.bind(this));
    }
  }

  save() {
    this.enableSaveButton = false;

    this.checkIfFormIsValid(this.isFormValid);

    if (!this.isFormValidAfterSave) {
      this.enableSaveButton = true;
      return;
    }

    this.client = Utils.checkObjectForEmptyValuesRecursive(this.client);

    if (!this.client.status) {
      this.notificationService.showLocalizedSnackbarNotification({
        notificationText: 'clients.selectClientStatusMessage',
      });
      this.enableSaveButton = true;
      return;
    }

    if (this.isNewItem) {
      this.client.idCardVerified = false;
    }

    return firstValueFrom(this.clientService.saveClient(this.client))
      .then((savedClient: Client) => {
        this.client = savedClient;
        this.isNewItem = false;

        this.profileComponentEl.profileDataForm.markAsPristine();
        this.personalIdComponentEl.profileDataForm.markAsPristine();
        this.personalComponentEl.personalDataForm.markAsPristine();
        this.occupationStatusComponentEl.occupationStatusForm.markAsPristine();

        const savePromises: Promise<any>[] = [];

        if (this.hasUnsavedContacts()) {
          savePromises.push(this.saveAllPendingContacts(savedClient.id));
        }

        if (this.hasUnsavedBankAccounts()) {
          savePromises.push(this.saveAllPendingBankAccounts(savedClient.id));
        }

        if (this.hasUnsavedDocuments()) {
          savePromises.push(this.saveAllPendingDocuments(savedClient.id));
        }

        return Promise.all(savePromises).then(() => savedClient);
      })
      .then((savedClient) => {
        this.outputClientData.emit(savedClient);
        this.clientService.onClientSaved.next(savedClient);

        if (!this.clientData || !this.clientData.id) {
          this.navigateToNextPage();
        }

        this.notificationService.showLocalizedSuccessMessage({
          notificationText: 'clients.successfullySavedClient',
        });

        return savedClient;
      })
      .catch((error: HttpErrorResponse) => {
        console.error('Client save failed:', error);
        this.errorService.showSnackbarErrorMessage(error);
        this.loaderService.display(false);
        this.enableSaveButton = true;
        return Promise.reject(error);
      })
      .finally(() => {
        this.enableSaveButton = false;
        this.clientDataChangesDetected = false;
      });
  }

  saveAllPendingContacts(clientId: number): Promise<ContactPhone[]> {
    if (!this.contactComponentEl) return Promise.resolve([]);

    const pendingContacts: { data: ContactPhoneDto, index: number }[] = [];

    this.contactComponentEl.contactForms.controls.forEach(
      (form: UntypedFormGroup, index: number) => {
        if (this.contactComponentEl.saveBtnEnabled[index] && form.valid) {
          pendingContacts.push({
            data: form.getRawValue(),
            index: index
          });
        }
      }
    );

    if (pendingContacts.length === 0) return Promise.resolve([]);

    const savePromises = pendingContacts.map(({ data, index }) => {
      if (data.id) {
        return firstValueFrom(
          this.contactService.editContact(clientId, data.id, data)
        ).then(updatedContact => ({ contact: updatedContact, index }));
      } else {
        return firstValueFrom(
          this.contactService.createContact(clientId, data)
        ).then(newContact => ({ contact: newContact, index }));
      }
    });

    return Promise.all(savePromises)
      .then((results) => {
        results.forEach(({ contact, index }) => {
          const newForm = this.contactComponentEl.createExistingContactForm(contact);
          this.contactComponentEl.contactForms.setControl(index, newForm);
          this.contactComponentEl.watchForFormChanges(newForm);
          this.contactComponentEl.saveBtnEnabled[index] = false;
        });

        return firstValueFrom(
          this.contactService.getContactList(clientId)
        ).then(contacts => {
          this.contactComponentEl.contactList = contacts;
          this.contactService.contactsSubject.next(contacts);

          if (this.eventLogService.clientLogToggled) {
            this.eventLogService.refreshEventLog.next();
          }
          return results.map(r => r.contact);
        });
      })
      .catch((error: HttpErrorResponse) => {
        console.error('Error saving contacts:', error);
        this.notificationService.showLocalizedErrorMessage({
          notificationText: 'communication.contactUnsuccessfullyAdded'
        });
        throw error;
      });
  }

  saveAllPendingBankAccounts(clientId: number): Promise<BankAccount[]> {
    if (!this.bankAccountComponentEl) return Promise.resolve([]);

    const pendingAccounts: { form: UntypedFormGroup, index: number }[] = [];

    this.bankAccountComponentEl.bankAccountForms.controls.forEach(
      (form: UntypedFormGroup, index: number) => {
        if (this.bankAccountComponentEl.saveBtnEnabled[index] && form.valid) {
          pendingAccounts.push({ form, index });
        }
      }
    );

    if (pendingAccounts.length === 0) return Promise.resolve([]);

    const savePromises = pendingAccounts.map(({ form, index }) => {
      const data = form.getRawValue();
      const formData = new FormData();

      if (data.userDocument instanceof File) {
        formData.append('file', data.userDocument);
      }

      if (data.id) {
        return firstValueFrom(
          this.clientService.updateClientBankAccount(clientId, data.id, {
            iban: data.iban,
            verified: data.verified,
            id: data.id
          }, formData)
        );
      } else {
        return firstValueFrom(
          this.clientService.saveClientBankAccount(clientId, {
            iban: data.iban,
            verified: data.verified
          }, formData)
        );
      }
    });

    return Promise.all(savePromises)
      .then((savedAccounts) => {
        pendingAccounts.forEach(({ index }) => {
          this.bankAccountComponentEl.saveBtnEnabled[index] = false;
        });

        savedAccounts.forEach((savedAccount, i) => {
          const originalIndex = pendingAccounts[i].index;
          const newForm = this.bankAccountComponentEl.createExistingBankAccountFormGroup(savedAccount);
          this.bankAccountComponentEl.bankAccountForms.setControl(originalIndex, newForm);
          this.bankAccountComponentEl.watchIndividualFormChanges(newForm, originalIndex);
        });

        return firstValueFrom(this.clientService.getClientBankAccounts(clientId))
          .then(bankAccounts => {
            this.clientComponentData.bankAccounts.bankAccounts = bankAccounts;

            bankAccounts.forEach(account => {
              if (account.userDocument) {
                const docExists = this.clientComponentData?.documents?.some(
                  (doc: ClientDocument) => doc.id === account.userDocument.id
                );
                if (!docExists) {
                  this.clientComponentData.documents.push(account.userDocument);
                }
              }
            });

            this.clientService.onBankAccoutDataChange.next(this.clientComponentData.bankAccounts);
            this.clientService.onDataChange.next(this.clientComponentData.documents);

            this.eventLogService.refreshEventLog.next();

            return savedAccounts;
          });
      })
      .catch((error: HttpErrorResponse) => {
        console.error('Error saving bank accounts:', error);
        this.notificationService.showLocalizedErrorMessage({
          notificationText: 'clients.createBankAccountError'
        });
        throw error;
      });
  }

  hasUnsavedContacts(): boolean {
    if (!this.contactComponentEl) return false;

    return this.contactComponentEl.saveBtnEnabled.some(
      (enabled: boolean, index: number) => {
        const form = this.contactComponentEl.contactForms.at(index) as UntypedFormGroup;
        return enabled && form.valid;
      }
    );
  }

  hasUnsavedBankAccounts(): boolean {
    if (!this.bankAccountComponentEl) {
      return false;
    }

    const hasUnsaved = this.bankAccountComponentEl.saveBtnEnabled.some(
      (enabled: boolean, index: number) => {
        const form = this.bankAccountComponentEl.bankAccountForms.at(index) as UntypedFormGroup;
        const result = enabled && form.valid;
        return result;
      }
    );

    return hasUnsaved;
  }

  hasUnsavedDocuments(): boolean {
    if (!this.documentsComponentEl) return false;
    return this.documentsComponentEl.hasUnsavedDocuments();
  }

  saveAllPendingDocuments(clientId: number): Promise<ClientDocument[]> {
    if (!this.documentsComponentEl) return Promise.resolve([]);

    return this.documentsComponentEl.saveAllPendingDocuments(clientId)
      .then(savedDocs => {
        savedDocs.forEach((doc: ClientDocument) => {
          const exists: boolean = this.clientComponentData?.documents.some((d: ClientDocument) => d.id === doc.id);
          if (!exists) {
            this.clientComponentData.documents.push(doc);
          }
        });

        this.clientService.onDataChange.next(this.clientComponentData.documents);

        this.eventLogService.refreshEventLog.next();

        return savedDocs;
      })
      .catch((error: HttpErrorResponse) => {
        console.error('Error saving documents:', error);
        this.notificationService.showLocalizedErrorMessage({
          notificationText: 'clients.unableToSaveDoc'
        });
        throw error;
      });
  }

  sendPasswordByEmail(event) {
    this.dialogService
      .openConfirm({
        message: this.translateService.instant(
          'clients.sendPasswordByEmailConfirmationMessage',
          { email: this.client.email }
        ),
        viewContainerRef: this.viewContainerRef,
        title: this.translateService.instant('global.confirm'),
        cancelButton: this.translateService.instant('global.cancel'),
        acceptButton: this.translateService.instant('global.confirm'),
      })
      .afterClosed()
      .pipe(filter((accept) => accept === true))
      .subscribe((accept: boolean) => {
        if (accept) {
          this.emailService
            .sendTemporaryPassword(this.client.id)
            .subscribe((email) => {
              this.notificationService.showLocalizedSuccessMessage({
                notificationText: 'communication.emailSuccessfullySent',
              });
              this.emailService.onEmailSend.next(email);
            });
        }
      });
  }

  sendPasswordBySms(event) {
    this.dialogService
      .openConfirm({
        message: this.translateService.instant(
          'clients.sendPasswordBySmsConfirmationMessage',
          { phoneNumber: this.client.mobile }
        ),
        viewContainerRef: this.viewContainerRef,
        title: this.translateService.instant('global.confirm'),
        cancelButton: this.translateService.instant('global.cancel'),
        acceptButton: this.translateService.instant('global.confirm'),
      })
      .afterClosed()
      .pipe(filter((accept) => accept === true))
      .subscribe((accept: boolean) => {
        if (accept) {
          this.smsService
            .sendTemporaryPassword(this.client.id)
            .subscribe((sms) => {
              this.notificationService.showLocalizedSuccessMessage({
                notificationText: 'communication.smsSuccessfullySent',
              });
              this.smsService.onSmsSend.next(sms);
            });
        }
      });
  }

  openCreateOfferDialog() {
    return this.dialog.open(ClientPricingOfferCreateDialogComponent, {
      data: {
        client: this.client,
      },
      width: '1100px',
      maxHeight: '80vh'
    })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getOffersByClientCivilId();
        }
      });
  }

  scrollToTop() {
    // Custom scroll to top on invalid form
    const currentElement = document.getElementsByClassName(
      'main-layout-container'
    )[0];
    const scrollToTop = window.setInterval(function () {
      const pos = currentElement.scrollTop;
      if (pos > 0 && pos > 400) {
        currentElement.scrollTo(0, pos - 60); // how far to scroll on each step
      } else if (pos > 0 && pos > 150) {
        currentElement.scrollTo(0, pos - 20); // how far to scroll on each step
      } else if (pos > 0) {
        currentElement.scrollTo(0, pos - 10); // how far to scroll on each step
      } else {
        window.clearInterval(scrollToTop);
      }
    }, 16); // how fast to scroll (this equals roughly 60 fps)
  }

  checkIfFormIsValid(isValidFormObject) {
    const formProperties = Object.keys(isValidFormObject).reverse();
    this.isFormValidAfterSave = true;

    for (const prop of formProperties) {
      if (!isValidFormObject[prop]) {
        this.invalidProfileData = true;
        this.notificationService.showLocalizedErrorMessage({
          notificationText: 'forms.formSavedIsInvalid',
        });
        switch (prop) {
          case 'profileData':
            this.markInvalidForm(
              this.profileComponentEl.profileDataForm,
              this.profileEl.nativeElement
            );
            break;
          case 'personalData':
            this.markInvalidForm(
              this.personalComponentEl.personalDataForm,
              this.personalEl.nativeElement
            );
            break;
          case 'idCardDataAndAddress':
            this.markInvalidForm(
              this.personalIdComponentEl.profileDataForm,
              this.personalIdEl.nativeElement
            );
            break;
          case 'occupationStatusData':
            this.markInvalidForm(
              this.occupationStatusComponentEl.occupationStatusForm,
              this.occupationStatusEl.nativeElement
            );
            break;
        }

        this.isFormValidAfterSave = false;
      }
    }
  }

  openLoyaltyPointsDetails() {
    this.loyaltyPointsService
      .getLoyaltyPointsFlow$(this.client.id)
      .subscribe((pointsFlow) => {
        this.loyaltyPointsFlow = pointsFlow;
        const dialogRef = this.dialog.open(LoyaltyPointsFlowComponent, {
          data: {
            received: this.loyaltyPointsFlow.received,
            used: this.loyaltyPointsFlow.used,
            clientId: this.client.id,
          },
          autoFocus: false,
          restoreFocus: false,
        });
        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            this.loyaltyPointsService
              .getLoyaltyAvailableNumber$(this.client.id)
              .subscribe((points) => {
                this.loyaltyPointsAvailableNumber = points;
              });
          }
        });
      });
  }

  focusTagSearchBarInput() {
    this.tagMenuComponentEl.focusSearchInput();
  }

  clientLogInit() {
    const client = this.client ? this.client : null;
    const phoneNumber = this.client.mobile;

    this.eventLogStateService.setState(
      {
        mode: EventLogMode.CLIENT,
        subheader: EventLogSubheader.PBX,
        historyType: EventLogHistoryType.PHONE,
        client: client,
        phoneNumber: phoneNumber,
      },
      {
        openDrawer: true,
        isInClientEdit: true,
      }
    );
  }

  markInvalidForm(dataForm, element) {
    this.formManipulationService.markAllControlsTouched(dataForm);
    // Utils.scrollElementToTop(element);
  }

  navigateToNextPage() {
    this.router.navigate(['clients', this.client.id]);
  }

  openAddClientTag() {
    const dialogRef = this.dialog.open(AddTagComponent, {
      data: {
        id: this.client.id,
        tags: this.client.tags,
        type: TagTypeNames.CLIENT,
      },
      restoreFocus: false,
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (options) => {
          if (options) {
            const tagUsers$ = this.tagService.tagUsers(
              [options.id],
              options.tagId
            );
            tagUsers$.subscribe({
              next: (res: void) => {
                this.client.tags.push(options.tagValue);
                this.notificationService.showLocalizedSuccessMessage({
                  notificationText: 'credits.tagSuccessfullyAdded',
                });
                this.tagService.onTagAddedOrDeleted.next(this.client.tags);
                this.tagService.onTagHttpRequest.next();
              },
              error: (error: HttpErrorResponse) => {
                this.errorService.showSnackbarErrorMessage(error);
              },
            });
          }
        },
      });
  }

  getOffersByClientCivilId() {
    const baseSearchOptions = new SearchOptions();
    baseSearchOptions.page = 0;
    baseSearchOptions.pageSize = 100;
    baseSearchOptions.direction = SearchDirection.DESCENDING;
    baseSearchOptions.column = 'id';
    baseSearchOptions.criteria = [
      {
        key: 'civilId',
        operation: SearchOperations.EQUALS,
        value: this.clientData?.civilId || this.client?.civilId,
      },
      {
        key: 'brand.code',
        operation: SearchOperations.EQUALS,
        value: this.clientData?.brand?.code || this.client?.brand?.code
      }
    ];

    const activeSearchOptions = new SearchOptions();
    activeSearchOptions.page = 0;
    activeSearchOptions.pageSize = 100;
    activeSearchOptions.direction = SearchDirection.DESCENDING;
    activeSearchOptions.column = 'id';
    activeSearchOptions.criteria = [
      ...baseSearchOptions.criteria,
      {
        key: 'cancelledAt',
        operation: SearchOperations.EQUALS,
        value: null
      },
      {
        key: 'validTo',
        operation: SearchOperations.GREATER_THAN_OR_EQUAL,
        value: new Date().toISOString()
      }
    ];

    forkJoin({
      allOffers: this.clientPricingOfferService.getClientPricingOfferList$(baseSearchOptions.getDTO()),
      activeOffers: this.clientPricingOfferService.getClientPricingOfferList$(activeSearchOptions.getDTO())
    })
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(({ allOffers, activeOffers }) => {
        this.clientOffers = [...allOffers.content];
        this.activeClientOffers = [...activeOffers.content];
      });
  }

  handleTagSelection(options: { id: number; tagId: number; tagValue: Tag }) {
    const tagUsers$ = this.tagService.tagUsers([options.id], options.tagId);

    tagUsers$.pipe(takeUntil(this._unsubscribe)).subscribe({
      next: (res: void) => {
        this.client.tags.push(options.tagValue);
        this.notificationService.showLocalizedSuccessMessage({
          notificationText: 'credits.tagSuccessfullyAdded',
        });
        this.tagService.onTagAddedOrDeleted.next(this.client.tags);
        this.tagService.onTagHttpRequest.next();
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.showSnackbarErrorMessage(error);
      },
    });
  }

  formChangesOtherThanContactForm(): boolean {
    const profileDataChanged = this.profileComponentEl.profileDataForm.dirty;
    const personalIdDataChanged =
      this.personalIdComponentEl.profileDataForm.dirty;
    const personalDataChanged = this.personalComponentEl.personalDataForm.dirty;
    const occupationStatusChanged =
      this.occupationStatusComponentEl.occupationStatusForm.dirty;

    return (
      profileDataChanged ||
      personalIdDataChanged ||
      personalDataChanged ||
      occupationStatusChanged
    );
  }

  ngOnDestroy() {
    this.eventLogStateService.exitEditComponent(FilterGroupType.CLIENT);
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
